import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../../firebase/config";

const recruitmentCollectionRef = collection(
  db,
  "otherDBs",
  "recruitments",
  "recruitments"
);

function toMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function normalizeCandidate(docSnap) {
  return { id: docSnap.id, ...docSnap.data() };
}

export const fetchAdminCandidates = createAsyncThunk(
  "recruitment/fetchAdminCandidates",
  async (candidateStatus = "live", { rejectWithValue }) => {
    try {
      const q = query(
        recruitmentCollectionRef,
        where("candidateStatus", "==", candidateStatus)
      );
      const snapshot = await getDocs(q);
      const rows = snapshot.docs.map(normalizeCandidate);
      rows.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
      return { candidateStatus, candidates: rows };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load candidates");
    }
  }
);

export const fetchAssessorCandidates = createAsyncThunk(
  "recruitment/fetchAssessorCandidates",
  async (assessorEmail, { rejectWithValue }) => {
    try {
      if (!assessorEmail) return [];
      const [oneSnap, twoSnap] = await Promise.all([
        getDocs(
          query(recruitmentCollectionRef, where("assessorOne", "==", assessorEmail))
        ),
        getDocs(
          query(recruitmentCollectionRef, where("assessorTwo", "==", assessorEmail))
        ),
      ]);
      const byId = new Map();
      oneSnap.docs.forEach((d) => byId.set(d.id, normalizeCandidate(d)));
      twoSnap.docs.forEach((d) => byId.set(d.id, normalizeCandidate(d)));
      const rows = Array.from(byId.values()).filter(
        (item) => item.candidateStatus === "live"
      );
      rows.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
      return rows;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to load assessor recruitment queue"
      );
    }
  }
);

export const addRecruitmentCandidate = createAsyncThunk(
  "recruitment/addRecruitmentCandidate",
  async ({ candidateData, cvFile }, { rejectWithValue }) => {
    try {
      if (candidateData.sourceApplicationId && candidateData.sourceJobId) {
        const sourceApplicationKey = `${candidateData.sourceJobId}:${candidateData.sourceApplicationId}`;
        const existingCandidateSnap = await getDocs(
          query(
            recruitmentCollectionRef,
            where("sourceApplicationKey", "==", sourceApplicationKey)
          )
        );
        if (!existingCandidateSnap.empty) {
          return rejectWithValue(
            "A recruitment candidate already exists for this shortlisted application."
          );
        }

        const sourceAppRef = doc(
          db,
          "recruitmentSystem",
          "jobPosts",
          "JobPosts",
          candidateData.sourceJobId,
          "applications",
          candidateData.sourceApplicationId
        );
        const sourceAppSnap = await getDoc(sourceAppRef);
        if (!sourceAppSnap.exists()) {
          return rejectWithValue("Source application not found.");
        }
        const sourceApp = sourceAppSnap.data();
        const allowedStatuses = new Set(["shortlisted", "interview", "hired"]);
        if (!allowedStatuses.has(String(sourceApp.status || "new"))) {
          return rejectWithValue(
            "Only shortlisted applications can be added to recruitment candidates."
          );
        }
      }

      let candidateCV = candidateData.candidateCV || "";
      if (cvFile) {
        const fileExt = cvFile.name.split(".").pop();
        const filePath = `recruitment-cvs/${Date.now()}-${cvFile.name.replace(
          /\s+/g,
          "_"
        )}.${fileExt || "pdf"}`;
        const storageRef = ref(storage, filePath);
        const uploadResult = await uploadBytes(storageRef, cvFile);
        candidateCV = await getDownloadURL(uploadResult.ref);
      }

      const payload = {
        ...candidateData,
        sourceApplicationKey:
          candidateData.sourceApplicationId && candidateData.sourceJobId
            ? `${candidateData.sourceJobId}:${candidateData.sourceApplicationId}`
            : "",
        candidateCV,
        candidateStatus: candidateData.candidateStatus || "live",
        adminChecked:
          typeof candidateData.adminChecked === "boolean"
            ? candidateData.adminChecked
            : false,
        createdAt: new Date(),
        lastUpdated: new Date(),
      };

      const docRef = await addDoc(recruitmentCollectionRef, payload);

      if (candidateData.sourceApplicationId && candidateData.sourceJobId) {
        const sourceAppRef = doc(
          db,
          "recruitmentSystem",
          "jobPosts",
          "JobPosts",
          candidateData.sourceJobId,
          "applications",
          candidateData.sourceApplicationId
        );
        const sourceAppSnap = await getDoc(sourceAppRef);
        if (sourceAppSnap.exists()) {
          const sourceApp = sourceAppSnap.data();
          const preservedStatuses = new Set(["interview", "hired"]);
          const nextStatus = preservedStatuses.has(String(sourceApp.status || ""))
            ? sourceApp.status
            : "interview";
          await updateDoc(sourceAppRef, {
            status: nextStatus,
            statusUpdatedAt: new Date(),
            updatedAt: new Date(),
            linkedRecruitmentCandidateId: docRef.id,
          });
        }
      }
      return { id: docRef.id, ...payload };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to add candidate");
    }
  }
);

export const fetchShortlistedApplicationsByJob = createAsyncThunk(
  "recruitment/fetchShortlistedApplicationsByJob",
  async (jobId, { rejectWithValue }) => {
    try {
      if (!jobId) return [];
      const applicationsRef = collection(
        db,
        "recruitmentSystem",
        "jobPosts",
        "JobPosts",
        jobId,
        "applications"
      );
      const snapshot = await getDocs(
        query(applicationsRef, where("status", "==", "shortlisted"))
      );
      return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch shortlisted job applications"
      );
    }
  }
);

export const submitAssessorReview = createAsyncThunk(
  "recruitment/submitAssessorReview",
  async (
    { candidateId, assessorSlot, score, review, strengths, weaknesses },
    { rejectWithValue }
  ) => {
    try {
      const payload =
        assessorSlot === "two"
          ? {
              assTwoReview: review,
              twoScore: Number(score || 0),
              twoSubmissionStrengths: strengths || "",
              twoSubmissionWeaknesses: weaknesses || "",
              lastUpdated: new Date(),
            }
          : {
              assOneReview: review,
              oneScore: Number(score || 0),
              oneSubmissionStrengths: strengths || "",
              oneSubmissionWeaknesses: weaknesses || "",
              lastUpdated: new Date(),
            };

      await updateDoc(doc(recruitmentCollectionRef, candidateId), payload);
      return { candidateId, payload };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to submit assessor review");
    }
  }
);

export const updateAdminReview = createAsyncThunk(
  "recruitment/updateAdminReview",
  async ({ candidateId, finalReview, finalRemarks, adminChecked }, { rejectWithValue }) => {
    try {
      const payload = {
        finalReview: finalReview || "Undecided",
        finalRemarks: finalRemarks || "",
        adminChecked: Boolean(adminChecked),
        lastUpdated: new Date(),
      };
      await updateDoc(doc(recruitmentCollectionRef, candidateId), payload);
      return { candidateId, payload };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update admin review");
    }
  }
);

export const reassignCandidateAssessors = createAsyncThunk(
  "recruitment/reassignCandidateAssessors",
  async ({ candidateId, assessorOne, assessorTwo }, { rejectWithValue }) => {
    try {
      const payload = {
        assessorOne: assessorOne || "",
        assessorTwo: assessorTwo || "",
        lastUpdated: new Date(),
      };
      await updateDoc(doc(recruitmentCollectionRef, candidateId), payload);
      return { candidateId, payload };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to reassign assessors");
    }
  }
);

export const setCandidateArchived = createAsyncThunk(
  "recruitment/setCandidateArchived",
  async ({ candidateId, archived }, { rejectWithValue }) => {
    try {
      const payload = {
        candidateStatus: archived ? "archived" : "live",
        lastUpdated: new Date(),
      };
      await updateDoc(doc(recruitmentCollectionRef, candidateId), payload);
      return { candidateId, payload };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to change candidate status");
    }
  }
);

const initialState = {
  adminCandidates: [],
  assessorCandidates: [],
  shortlistedApplications: [],
  adminStatusFilter: "live",
  loadingAdmin: false,
  loadingAssessor: false,
  mutating: false,
  error: null,
};

const recruitmentSlice = createSlice({
  name: "recruitment",
  initialState,
  reducers: {
    clearRecruitmentError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminCandidates.pending, (state) => {
        state.loadingAdmin = true;
        state.error = null;
      })
      .addCase(fetchAdminCandidates.fulfilled, (state, action) => {
        state.loadingAdmin = false;
        state.adminCandidates = action.payload.candidates;
        state.adminStatusFilter = action.payload.candidateStatus;
      })
      .addCase(fetchAdminCandidates.rejected, (state, action) => {
        state.loadingAdmin = false;
        state.error = action.payload || "Failed to fetch admin candidates";
      })
      .addCase(fetchAssessorCandidates.pending, (state) => {
        state.loadingAssessor = true;
        state.error = null;
      })
      .addCase(fetchAssessorCandidates.fulfilled, (state, action) => {
        state.loadingAssessor = false;
        state.assessorCandidates = action.payload;
      })
      .addCase(fetchAssessorCandidates.rejected, (state, action) => {
        state.loadingAssessor = false;
        state.error = action.payload || "Failed to fetch assessor candidates";
      })
      .addCase(fetchShortlistedApplicationsByJob.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchShortlistedApplicationsByJob.fulfilled, (state, action) => {
        state.shortlistedApplications = action.payload;
      })
      .addCase(fetchShortlistedApplicationsByJob.rejected, (state, action) => {
        state.shortlistedApplications = [];
        state.error =
          action.payload || "Failed to fetch shortlisted job applications";
      });

    const mutationThunks = [
      addRecruitmentCandidate,
      submitAssessorReview,
      updateAdminReview,
      reassignCandidateAssessors,
      setCandidateArchived,
    ];

    mutationThunks.forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.mutating = true;
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state) => {
          state.mutating = false;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.mutating = false;
          state.error = action.payload || "Recruitment operation failed";
        });
    });
  },
});

export const { clearRecruitmentError } = recruitmentSlice.actions;
export default recruitmentSlice.reducer;
